import {
  Label,
  ListPage,
  RefreshButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@skylabs-digital/backoffice-kit';
import { usePolling } from '@skylabs-digital/backoffice-kit/hooks';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { appsApi } from '../api/apps.api';
import type { FilesPage } from '../api/files.api';
import { useProcessingActions } from '../api/processing.api';
import { ProcessingByAppTable } from '../components/processing/ProcessingByAppTable';
import { ProcessingCharts } from '../components/processing/ProcessingCharts';
import { ProcessingFailuresTable } from '../components/processing/ProcessingFailuresTable';
import { ProcessingKpis } from '../components/processing/ProcessingKpis';
import type { FistoFile } from '../schemas/file.schema';
import {
  type ProcessingOverview,
  type ProcessingSeries,
  type ProcessingWindow,
  processingWindowSchema,
} from '../schemas/processing.schema';

const REFRESH_MS = 15_000;
const ALL = '__all__';
const LIMIT = 25;
const WINDOWS: ProcessingWindow[] = ['1h', '6h', '24h', '7d'];

export function ProcessingPage() {
  const api = useProcessingActions();
  const { showSnackbar } = useSnackbar();
  const { data: apps } = appsApi.useList();
  const [params, setParams] = useSearchParams();
  const appId = params.get('app') ?? undefined;
  const parsedWindow = processingWindowSchema.safeParse(params.get('window'));
  const window: ProcessingWindow = parsedWindow.success ? parsedWindow.data : '24h';
  const [live, setLive] = useState(true);
  const [page, setPage] = useState(1);
  const [overview, setOverview] = useState<ProcessingOverview | null>(null);
  const [series, setSeries] = useState<ProcessingSeries | null>(null);
  const [failures, setFailures] = useState<FilesPage | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const setParam = (key: 'app' | 'window', value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  const load = useCallback(async () => {
    try {
      const [o, s, f] = await Promise.all([
        api.overview({ window, appId }),
        api.series({ window, appId }),
        api.failures({ appId, page, limit: LIMIT }),
      ]);
      setOverview(o);
      setSeries(s);
      setFailures(f);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load processing stats'));
    }
  }, [api, window, appId, page]);

  const { tick, running } = usePolling(load, REFRESH_MS, { enabled: live });
  // usePolling ya carga al montar; un cambio de filtro o página recarga aunque
  // el polling esté apagado (el hook guarda `fn` en un ref y no reinicia).
  const mounted = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: los filtros son el disparador; `tick` es estable.
  useEffect(() => {
    if (mounted.current) void tick();
    else mounted.current = true;
  }, [window, appId, page]);

  const retryOne = useCallback(
    async (f: FistoFile) => {
      try {
        await api.retry(f.appId, f.id);
        showSnackbar({ message: `Queued ${f.path} again`, variant: 'success' });
        await tick();
      } catch {
        showSnackbar({ message: 'Could not retry this file', variant: 'error' });
      }
    },
    [api, showSnackbar, tick],
  );
  const retryAll = async () => {
    const rows = failures?.data ?? [];
    const results = await Promise.allSettled(rows.map((f) => api.retry(f.appId, f.id)));
    const failedCount = results.filter((r) => r.status === 'rejected').length;
    showSnackbar({
      message: failedCount
        ? `${rows.length - failedCount} queued, ${failedCount} could not be retried`
        : `Queued ${rows.length} files again`,
      variant: failedCount ? 'error' : 'success',
    });
    await tick();
  };

  return (
    <ListPage
      title="Processing"
      description="The image variants queue: what is waiting, how fast it drains and what failed."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={appId ?? ALL}
            onValueChange={(v) => setParam('app', v === ALL ? undefined : v)}
          >
            <SelectTrigger className="w-48" aria-label="App">
              <SelectValue placeholder="All apps" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All apps</SelectItem>
              {(apps ?? []).map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Tabs value={window} onValueChange={(v) => setParam('window', v)}>
            <TabsList>
              {WINDOWS.map((w) => (
                <TabsTrigger key={w} value={w}>
                  {w}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch id="live" checked={live} onCheckedChange={setLive} />
            <Label htmlFor="live">Auto-refresh</Label>
          </span>
          <RefreshButton onRefresh={tick} isRefreshing={running} label={null} />
        </div>
      }
    >
      <div className="space-y-6">
        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}
        <ProcessingKpis overview={overview} loading={running} />
        <ProcessingCharts series={series} loading={running} />
        <section className="space-y-2">
          <h2 className="text-base font-semibold">By app</h2>
          <ProcessingByAppTable
            rows={overview?.byApp ?? []}
            onSelectApp={(id) => setParam('app', id)}
          />
        </section>
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Failed files</h2>
          <ProcessingFailuresTable
            page={failures}
            loading={running && failures === null}
            error={error}
            onRetry={retryOne}
            onRetryAll={retryAll}
            onPage={setPage}
          />
        </section>
      </div>
    </ListPage>
  );
}
